/**
 * What a personal tenant IS on this deployment (0175, Phase D).
 *
 * ── Three states, and the third is real here ────────────────────────────
 *
 * `'full'` and `'identity-only'` are chosen; `null` means *use the shipped
 * default*, which resolves to `'full'` and is the state of every deployment
 * until someone touches this. Unlike the landing control — whose third state
 * defers to a brand file — the thing being deferred to here is a constant, so
 * the inherit row can name the outcome flatly ("Full") rather than reading a
 * value off the server.
 *
 * It is still a real, selectable position and must not collapse into `'full'`.
 * Storing an explicit `'full'` where the operator meant "leave it alone" is a
 * decision recorded as if it were deliberate, and the audit row then says an
 * operator chose the full product on a day they chose nothing.
 *
 * ── Why cards, and what the previews actually draw ──────────────────────
 *
 * Copied from `LandingSetting`, for the same reason: two words most operators
 * have never deliberately compared. But the drawing carries more weight here
 * than it does there, because the single most damaging misreading of this
 * feature is *"identity-only locks people out"*. It does not — the person signs
 * in, keeps their account, works inside organizations, and uses apps and shares
 * owned by full tenants. They lose their own Drive and nothing else.
 *
 * So both previews draw the SAME shell, the same organization, the same files.
 * The only difference on screen is the "My Drive" entry in the sidebar. That is
 * literally the difference in the product, and a preview that showed an empty
 * or greyed-out screen for identity-only would teach the misreading the copy
 * then has to argue against.
 *
 * ── The impact confirmation, and why it is asymmetric ───────────────────
 *
 * §4.1 requires a confirmation before a global change, with a CONCRETE count —
 * "this affects every personal tenant whose override is unset" is a sentence an
 * operator cannot size, and what is being sized is a Drive going out of reach
 * for that many people.
 *
 * The two directions are not the same act, so they do not get the same dialog.
 * Restricting takes something away from N people and is `danger`; relaxing
 * gives it back and is not. A single dialog phrased for the worse case would
 * cry wolf on the safe direction, and operators who learn to click through a
 * warning have lost the warning for the case that mattered.
 *
 * Both name the count NOT reached as well, because the two numbers differ and
 * an unexplained gap reads as a bug in the count.
 *
 * ⚠ The interpolation variable is `reached`, deliberately NOT `count`. i18next
 * treats `count` as the PLURALIZATION trigger: passing it makes the lookup go
 * looking for `…_one` / `…_other` before the base key, so a plain key
 * interpolated with `count` is one i18next version away from rendering nothing.
 * `locale-parity.test.ts` enforces that any key with a plural suffix carries the
 * whole set — this sidesteps the machinery instead of feeding it.
 *
 * ── No "takes effect within a minute" line ──────────────────────────────
 *
 * `LandingSetting` carries one and is right to: its value is read once per page
 * load. This has no cache anywhere in the resolution path — §6.3 requires the
 * mode to be re-read when work EXECUTES — so the honest statement is
 * "immediately", and a reassuring hedge would be a lie in the direction that
 * makes an operator flip it twice.
 */
import { useState } from 'react'
import { useTranslation } from '@papan/i18n'
import { useToast, useConfirm, useDraftState, CARD, SECTION_LABEL } from '@papan/core-ui/ui'
import { type TenantMode } from '@papan/types'
import { budgetApi, type PlatformSettingsResponse } from '../../client/budget'

/** Same non-collision rule as `LandingSetting`: `outline` composes with `CARD`'s
 *  own ring, where a second `ring-*` would race it in the generated stylesheet
 *  and silently lose. */
const SELECTED = 'outline outline-2 outline-[color:var(--bill-primary)]'
const SELECTABLE = 'hover:outline hover:outline-1 hover:outline-[color:var(--border-secondary)]'

/** Inline rather than `lucide-react` — `@papan/orgs` deliberately does not
 *  depend on it (see `LandingSetting`). */
function CheckMark() {
  return (
    <svg
      viewBox="0 0 16 16" width={15} height={15} aria-hidden
      className="ml-auto shrink-0 text-[color:var(--bill-primary)]"
      fill="none" stroke="currentColor" strokeWidth={2}
      strokeLinecap="round" strokeLinejoin="round"
    >
      <path d="M3 8.5 6.5 12 13 4.5" />
    </svg>
  )
}

function Bar({ w, className = '' }: { w: string; className?: string }) {
  return <div className={`h-1 rounded-full bg-[color:var(--border-secondary)] ${w} ${className}`} />
}

/**
 * The drive shell, drawn once and differing in exactly one row.
 *
 * `personal` controls whether the sidebar carries a "My Drive" entry above the
 * organization. Everything else — the taskbar rail, the org, the file grid — is
 * identical in both previews on purpose: the operator is being asked about one
 * row, so one row is what changes on screen.
 */
function DrivePreview({ personal }: { personal: boolean }) {
  return (
    <div className="aspect-[16/10] w-full rounded-lg overflow-hidden ring-1 ring-[color:var(--border-primary)] bg-[color:var(--bg-primary)] flex gap-1.5 p-1.5">
      <div className="w-3 shrink-0 rounded bg-[color:var(--bg-tertiary)] flex flex-col items-center gap-1 py-1">
        <div className="h-1.5 w-1.5 rounded-[2px] bg-[color:var(--border-secondary)]" />
        <div className="h-1.5 w-1.5 rounded-[2px] bg-[color:var(--border-secondary)]" />
        <div className="h-1.5 w-1.5 rounded-[2px] bg-[color:var(--border-secondary)]" />
      </div>

      {/* The sidebar — the only thing that differs. */}
      <div className="w-1/3 shrink-0 rounded bg-[color:var(--bg-secondary)] p-1 flex flex-col gap-1">
        {personal
          ? (
            // Tinted, because it is the subject of the question.
            <div className="flex items-center gap-1">
              <div className="h-1.5 w-1.5 rounded-[2px] bg-[color:var(--bill-primary)] shrink-0" />
              <div className="h-1 flex-1 rounded-full bg-[color:var(--bill-primary)] opacity-70" />
            </div>
          )
          : (
            // A gap of exactly the missing row's height, so the two previews
            // stay aligned and the absence reads as removal rather than as a
            // different layout.
            <div className="h-1.5" aria-hidden />
          )}
        <Bar w="w-5/6" />
        <Bar w="w-2/3" />
        <Bar w="w-3/4" />
      </div>

      {/* The org's files — present in BOTH. This is the half that says
          "identity-only is not a lockout". */}
      <div className="flex-1 rounded bg-[color:var(--bg-secondary)] p-1.5">
        <Bar w="w-1/4" />
        <div className="mt-1.5 grid grid-cols-3 gap-x-1.5 gap-y-1">
          {[0, 1, 2, 3, 4, 5].map(i => (
            <div key={i} className="flex flex-col items-center gap-0.5">
              <div className="w-full aspect-[4/3] rounded-[3px] bg-[color:var(--bg-tertiary)] ring-1 ring-[color:var(--border-secondary)]" />
              <Bar w="w-2/3" className="h-0.5" />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export function PersonalTenantModeSetting({
  data, canEdit, onSaved,
}: {
  data: PlatformSettingsResponse
  canEdit: boolean
  onSaved: () => void
}) {
  const { t } = useTranslation('core-ui')
  const toast = useToast()
  const confirm = useConfirm()

  const stored = data.settings.defaultPersonalTenantMode ?? null
  // A draft over `stored` (#897, kept across a phone/desktop layout swap):
  // until a card is clicked it IS `stored`, so a reload shows what PERSISTED;
  // once clicked, no reload overwrites the click. A successful save drops it
  // (the reload then shows what persisted); Discard drops it.
  const [choice, setChoice, { clear: clearChoice, settle: settleChoice }] = useDraftState<TenantMode | null>('platform:personalMode', stored)
  const [saving, setSaving] = useState(false)

  const dirty = choice !== stored
  // What a personal tenant would BE under the pending selection. `null` resolves
  // to 'full', so the confirmation below is about the outcome, not the click.
  const pending: TenantMode = choice ?? 'full'
  const current: TenantMode = data.resolved.defaultPersonalTenantMode

  // `??` guards a browser served by CF Pages before a rolling restart has
  // brought every API instance up on the field — the same real case
  // `LandingSetting` guards. Reading `.x` off an absent `advisory` throws and
  // would take the whole settings page down.
  const reached = data.advisory?.personalTenantsInheritingMode ?? null
  const notReached = data.advisory?.personalTenantsWithModeOverride ?? null

  const label = (m: TenantMode) =>
    m === 'identity-only'
      ? t('settings.platform.settings.modeIdentityOnly', { defaultValue: 'Identity-only' })
      : t('settings.platform.settings.modeFull', { defaultValue: 'Full' })

  const blurb = (m: TenantMode) =>
    m === 'identity-only'
      ? t('settings.platform.settings.modeIdentityOnlyBlurb', {
          defaultValue:
            'People sign in, keep their account, and work inside organizations. They have no personal Drive of their own.',
        })
      : t('settings.platform.settings.modeFullBlurb', {
          defaultValue:
            'The full product. Everyone gets a personal Drive alongside any organizations they belong to.',
        })

  const save = async () => {
    // Only when the OUTCOME moves. Storing an explicit 'full' over an inherited
    // 'full' changes nothing for anyone, so a danger dialog there would be the
    // cry-wolf this control is trying to avoid.
    if (pending !== current) {
      const restricting = pending === 'identity-only'
      const ok = await confirm({
        title: restricting
          ? t('settings.platform.settings.modeConfirmRestrictTitle', {
              defaultValue: 'Take away personal Drives?',
            })
          : t('settings.platform.settings.modeConfirmRelaxTitle', {
              defaultValue: 'Give everyone a personal Drive?',
            }),
        message: restricting
          ? t('settings.platform.settings.modeConfirmRestrict', {
              defaultValue:
                '{{reached}} personal tenants have no override of their own and lose their Drive immediately. Nothing is deleted — their files stay exactly where they are and come back if you switch this setting back. {{kept}} tenants carry their own setting and are NOT affected.',
              reached: reached ?? 0, kept: notReached ?? 0,
            })
          : t('settings.platform.settings.modeConfirmRelax', {
              defaultValue:
                '{{reached}} personal tenants have no override of their own and get their Drive back immediately, with their existing files intact. {{kept}} tenants carry their own setting and are NOT affected.',
              reached: reached ?? 0, kept: notReached ?? 0,
            }),
        confirmText: restricting
          ? t('settings.platform.settings.modeConfirmRestrictOk', { defaultValue: 'Switch to identity-only' })
          : t('settings.platform.settings.modeConfirmRelaxOk', { defaultValue: 'Switch to full' }),
        // Only the restricting direction. Giving a Drive back takes nothing
        // away, so it is `info` rather than a second red dialog.
        //
        // Deliberately NOT `requiredInput`. A type-to-confirm gate says "this
        // cannot be undone", and the whole design of this feature is that it
        // can: nothing is deleted, and switching back restores every Drive with
        // no repair pass (§9). Demanding the ceremony of an irreversible act
        // would teach the opposite of the guarantee.
        type: restricting ? 'danger' : 'info',
      })
      if (!ok) return
    }

    setSaving(true)
    try {
      // `null`, never `''` — the route validates against the closed union and an
      // empty string is a 400, not a clear.
      await budgetApi.setPlatformSettings({ defaultPersonalTenantMode: choice })
      settleChoice()
      toast.success(t('settings.platform.settings.modeSaved', { defaultValue: 'Default updated.' }))
      onSaved()
    } catch (e: any) {
      toast.error(e?.message || 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  return (
    <section className="mb-8">
      <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3 mb-4">
        <div className="min-w-0">
          <h3 className={`${SECTION_LABEL} block`}>
            {t('settings.platform.settings.modeTitle', { defaultValue: 'Personal tenants' })}
          </h3>
          <p className="text-sm themed-text-muted mt-1 max-w-xl">
            {t('settings.platform.settings.modeDesc', {
              defaultValue:
                'What a personal tenant is on this deployment. Everyone still gets one — it is their identity, and it is what lets them be shared with and switch between organizations. This decides whether it is also a workspace.',
            })}
          </p>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {/* In force, WITH its source. Without the source an operator sees a
              live behaviour beside a control they never set and cannot tell
              configured from inherited. */}
          <div
            className="rounded-xl px-3 py-2 bg-[color:var(--bill-tint)] ring-1 ring-[color:var(--bill-ring)]"
            title={
              data.resolved.defaultPersonalTenantModeSource === 'setting'
                ? t('settings.platform.settings.srcSetting', { defaultValue: 'Set here.' })
                : t('settings.platform.settings.srcDefault', {
                    defaultValue: 'The shipped default — nothing has been configured.',
                  })
            }
          >
            <div className="text-[10px] uppercase tracking-wide themed-text-muted">
              {t('settings.platform.settings.graceInForce', { defaultValue: 'In force' })}
            </div>
            <div className="text-sm font-medium text-[color:var(--bill-primary)] leading-tight mt-0.5">
              {label(current)}
            </div>
          </div>

          {/* How many this control actually reaches. Shown BEFORE the operator
              commits, not only in the dialog, because the size of the blast
              radius is part of deciding — not part of confirming. */}
          <div
            className="rounded-xl px-3 py-2 themed-bg-secondary ring-1 ring-[color:var(--border-primary)]"
            title={t('settings.platform.settings.modeReachedHint', {
              defaultValue:
                'Personal tenants with no override of their own. Tenants carrying an explicit setting keep it and are not affected by this control.',
            })}
          >
            <div className="text-[10px] uppercase tracking-wide themed-text-muted">
              {t('settings.platform.settings.modeReached', { defaultValue: 'This reaches' })}
            </div>
            <div className="text-sm font-medium themed-text leading-tight mt-0.5 tabular-nums">
              {reached == null ? '—' : String(reached)}
            </div>
          </div>
        </div>
      </div>

      <div
        role="radiogroup"
        aria-label={t('settings.platform.settings.modeTitle', { defaultValue: 'Personal tenants' })}
        className="grid grid-cols-1 sm:grid-cols-2 gap-4"
      >
        {(['full', 'identity-only'] as const).map(kind => {
          const active = choice === kind
          return (
            <button
              key={kind}
              role="radio"
              aria-checked={active}
              onClick={() => setChoice(kind)}
              disabled={saving || !canEdit}
              data-testid={`personal-mode-option-${kind}`}
              className={`${CARD} relative text-left p-3 transition-all disabled:cursor-default ${
                active ? SELECTED : canEdit ? SELECTABLE : 'opacity-70'
              }`}
            >
              <DrivePreview personal={kind === 'full'} />

              <div className="flex items-center gap-2 mt-3">
                <span className="text-sm font-semibold themed-text">{label(kind)}</span>
                {kind === 'full' && (
                  <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full themed-bg-tertiary themed-text-muted">
                    {t('settings.platform.settings.modeShipped', { defaultValue: 'Shipped default' })}
                  </span>
                )}
                {active && <CheckMark />}
              </div>
              <p className="text-xs themed-text-muted mt-1">{blurb(kind)}</p>
            </button>
          )
        })}

        {/* The third state as a ROW, not a card: it is not a third kind of
            tenant — it resolves to one of the two above, so a card would draw a
            preview identical to its neighbour and read as "three ways this can
            work". Same reasoning, and same shape, as `LandingSetting`. */}
        <button
          role="radio"
          aria-checked={choice === null}
          onClick={() => setChoice(null)}
          disabled={saving || !canEdit}
          data-testid="personal-mode-option-inherit"
          className={`${CARD} sm:col-span-2 flex items-start gap-3 text-left px-4 py-3 transition-all disabled:cursor-default ${
            choice === null ? SELECTED : canEdit ? SELECTABLE : 'opacity-70'
          }`}
        >
          <span
            className={`mt-0.5 h-4 w-4 shrink-0 rounded-full ring-1 flex items-center justify-center ${
              choice === null
                ? 'ring-[color:var(--bill-primary)]'
                : 'ring-[color:var(--border-secondary)]'
            }`}
            aria-hidden
          >
            {choice === null && <span className="h-2 w-2 rounded-full bg-[color:var(--bill-primary)]" />}
          </span>
          <span className="min-w-0">
            <span className="block text-sm themed-text">
              {/* Names the outcome. Unlike the landing control's equivalent, the
                  value is a constant rather than a brand-file read, so it is
                  written here rather than fetched. */}
              {t('settings.platform.settings.modeInherit', {
                defaultValue: 'Use the Papan default (Full)',
              })}
            </span>
            <span className="block text-xs themed-text-muted mt-0.5">
              {t('settings.platform.settings.modeInheritMeans', {
                defaultValue: 'No setting stored — this deployment behaves the way Papan ships.',
              })}
            </span>
          </span>
        </button>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
        {/* "Immediately", stated flatly. There is no cache between this setting
            and enforcement — §6.3 requires the mode to be resolved when work
            executes — so a "within a minute" hedge would be a lie in the
            reassuring direction. */}
        <p className="themed-text-muted text-[11px] max-w-xl">
          {t('settings.platform.settings.modeImmediate', {
            defaultValue:
              'Takes effect immediately, on the next request each person makes. Nothing is deleted either way: switching back restores every Drive exactly as its owner left it.',
          })}
        </p>

        {canEdit && dirty && (
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={clearChoice}
              data-testid="personal-mode-discard"
              disabled={saving}
              className="px-3 py-1.5 text-xs rounded-lg themed-text-secondary hover:themed-text disabled:opacity-50"
            >
              {t('settings.budget.pricing.discard', { defaultValue: 'Discard' })}
            </button>
            <button
              onClick={save}
              // Its LABEL changes while saving, which e2e/CLAUDE.md names as the
              // case requiring a test id rather than a role matcher.
              data-testid="personal-mode-save"
              disabled={saving}
              className="px-4 py-2 text-xs font-medium rounded-lg bg-gradient-to-r from-[#ddb8ff] to-[#0566d9] text-white hover:brightness-110 transition-all active:scale-[0.98] disabled:opacity-50 disabled:active:scale-100"
            >
              {saving ? t('common:saving') : t('common:save')}
            </button>
          </div>
        )}
      </div>
    </section>
  )
}
